import * as React from 'react';
import { Helmet } from 'react-helmet';

type AuthorsList = { name: string; link?: string }[];

interface GoogleNewsSchemaDataProps {
  canonical: string;
  title: string;
  image: string;
  datePublished: string;
  authors: AuthorsList | null;
  publisherName: string;
  logoUrl: string;
  dateModified: string;
  seoDescription: string;
  articleSection: string;
}

const dateToIsoDate = (date: string) => {
  return (new Date(date)).toISOString();
};

export const getAuthorsListSchema = (authors: AuthorsList) => {
  if (authors.length === 1) {
    return {
      '@type': 'Person',
      name: authors[0].name,
      ...authors[0].link ? { url: authors[0].link } : {},
    };
  }

  return authors.map(author => ({
    '@type': 'Person',
    name: author.name,
    ...author.link ? { url: author.link } : {},
  }));
};

const getSchemaData = (
  canonical: string,
  title: string,
  image: string,
  datePublished: string,
  publisherName: string,
  logoUrl: string,
  dateModified: string,
  seoDescription: string,
  articleSection: string,
) => {
  return {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    headline: title,
    image,
    datePublished: dateToIsoDate(datePublished),
    dateModified: dateToIsoDate(dateModified),
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    description: seoDescription,
    articleSection,
  };
};

export const GoogleNewsSchema: React.FunctionComponent<GoogleNewsSchemaDataProps> = props => {
  const { canonical, title, image, datePublished, authors, publisherName, logoUrl, dateModified, seoDescription, articleSection } = props;
  const schemaWithoutAuthors = getSchemaData(canonical, title, image, datePublished, publisherName, logoUrl, dateModified, seoDescription, articleSection);
  const schema = authors ? { ...schemaWithoutAuthors, author: getAuthorsListSchema(authors) } : schemaWithoutAuthors;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
